'use client';

import type { LatLngLiteralVerbose } from '@googlemaps/google-maps-services-js';
import { useEffect, useMemo, useState } from 'react';
import { fetchFromDealerLocator } from '../../utils/fetchFromDealerLocator';
import { isValidLatitudeLongitude } from '../../utils/isValidCoordinates';
import { PLACE_COORDINATES_ENDPOINT } from '../constants/apiEndpoints';
import { useStore } from '../providers/StoreProvider';

type PlaceCoordinatesResponse = {
  status: number;
  error: string;
  data?: { coordinates: LatLngLiteralVerbose; zipCodes: string[] };
};
type PlaceCoordinatesError = {
  status: number;
  error: string;
};

type UseCoordinatesProps = {
  coordinates?: {
    longitude: number;
    latitude: number;
  };
  place_id?: string | undefined;
};

const fetchPlaceCoordinates = (place_id: string, sessiontoken: string) => {
  return fetchFromDealerLocator<
    PlaceCoordinatesResponse,
    PlaceCoordinatesError
  >(PLACE_COORDINATES_ENDPOINT, {
    place_id,
    sessiontoken,
  });
};

export const useCoordinates = ({
  coordinates: initialCoordinates,
  place_id,
}: UseCoordinatesProps) => {
  // TODO: session token and refresh here instead of passing as params
  const [data, setData] = useState<PlaceCoordinatesResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<PlaceCoordinatesError>();
  const { sessiontoken, refreshSessionToken } = useStore();

  const isValidCoordinates = useMemo(
    () => initialCoordinates && isValidLatitudeLongitude(initialCoordinates),
    [initialCoordinates],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing isValidCoordinates should retrigger
  useEffect(() => {
    const fetchCoords = async () => {
      if (!place_id) {
        return;
      }
      setIsLoading(true);
      const { data: newData, error: newError } = await fetchPlaceCoordinates(
        place_id,
        sessiontoken,
      );
      refreshSessionToken();
      setData(newData);
      setError(newError);
      setIsLoading(false);
    };
    void fetchCoords();
  }, [place_id, isValidCoordinates]);

  const coordinates = useMemo(
    () => initialCoordinates || data?.data?.coordinates,
    [initialCoordinates, data?.data?.coordinates],
  );
  return {
    coordinates,
    isLoading,
    error,
  };
};
